import React from "react";
import ReactApexChart from "react-apexcharts";
import Utils from "Utils/utils";
import { getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownList from "./MemberBreakdownList";

/**
 * apexcharts can throw during its own destroy()/cleanup when the chart is
 * unmounted (e.g. switching to KPI, or a filter change swapping it out for a
 * skeleton) — see ErrorBoundary.js's note on this same failure class.
 * Without a boundary here that throw bubbles to the page-level ErrorBoundary
 * and kills the whole dashboard over one widget's chart.
 *
 * This boundary must itself stay mounted across bar/line/KPI switches — it's
 * placed once, outside the conditional that swaps the chart out — otherwise
 * it would be torn down in the same commit as the crashing chart and the
 * error would be attributed to the next boundary up (the page-level one)
 * instead of caught here. Resets via a `resetKey` prop rather than `key`,
 * mirroring ErrorBoundary.js, for the same reason.
 */
class ChartFailSafe extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetFor: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && (this.state.error || this.state.resetFor !== null)) {
      this.setState({ error: null, resetFor: null });
    }
  }
  componentDidCatch(error, info) {
    this.setState({ resetFor: this.props.resetKey });
    console.error("[ContactStagesWidget] chart crashed", error, info && info.componentStack);
  }
  render() {
    if (this.state.error) {
      return <p className="contactStagesEmpty">Chart unavailable</p>;
    }
    return this.props.children;
  }
}

const toMemberTotals = rows => {
  if (!rows?.length) return [];
  const byMember = {};
  rows.forEach(r => {
    if (!byMember[r.memberId]) {
      byMember[r.memberId] = { memberId: r.memberId, name: r.name, count: 0 };
    }
    byMember[r.memberId].count += r.count || 0;
  });
  return Object.values(byMember);
};

const COLOR = "#3953fb";
// Context grey for the bars that are not the point. Emphasis colouring — one
// accented bar among grey ones — is what makes a chart say something; six bars
// in one hue say "here is some data" and nothing more.
const MUTED = "#c7cbd6";

// Module-level, not inline in the options object: react-apexcharts only calls
// apexcharts' updateOptions() when a deep-equal check finds the options object
// changed, and a function value is compared by reference, not by what it does.
// An inline arrow function here is a fresh reference every render, so it would
// make that check see "changed" on every single re-render — including ones
// where nothing chart-relevant actually changed — firing updateOptions() far
// more often than necessary against apexcharts' async update pipeline.
const formatTooltipY = val => `${val} contacts`;

const humanizeStage = code =>
  (code || "")
    .split("_")
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
const stageDisplay = s => s.stageLabel || humanizeStage(s.stage);

/**
 * `showAxes` reveals the category and value labels. The dashboard hides them —
 * the card is small and the legend lives in WidgetCard — but a chart in the
 * chat has no surrounding card to name its bars, so an unlabelled chart there
 * is just coloured rectangles. Defaults to the dashboard's behaviour.
 */
const ContactStagesWidget = ({
  contactStages = [],
  memberBreakdown,
  breakdownLoading,
  visualization,
  showAxes = false,
  // `highlightStage` accents ONE bar and greys the rest. `onSelect` makes a bar
  // clickable, turning the chart into navigation ("show me those 534 bounces")
  // rather than a dead end. Both default off, so the dashboard is unchanged.
  highlightStage = null,
  onSelect = null
}) => {
  if (!contactStages.length) {
    return <p className="contactStagesEmpty">No contact stage data</p>;
  }

  const labels = contactStages.map(stageDisplay);
  const counts = contactStages.map(s => s.count);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";

  const chartBody = isKpi ? (
    <div className="widgetCardNumericSummary">
      {contactStages.map(s => (
        <div
          key={s.stage}
          className={`widgetCardNumericSummaryRow${
            highlightStage && s.stage === highlightStage ? " is-emphasis" : ""
          }${onSelect ? " is-clickable" : ""}`}
          onClick={onSelect ? () => onSelect(s) : undefined}
        >
          <span className="widgetCardNumericSummaryLabel">{stageDisplay(s)}</span>
          <span className="widgetCardNumericSummaryValue">{Utils.formatNumberToStringInUSFormat(s.count)}</span>
        </div>
      ))}
    </div>
  ) : (
    (() => {
      const options = {
        chart: {
          type: isLine ? "area" : "bar",
          toolbar: { show: false },
          animations: { enabled: false },
          selection: { enabled: false },
          zoom: { enabled: false },
          // Omitted entirely (not set to `undefined`) when there's no handler.
          // apexcharts' internal lifecycle code indexes into chart.events by
          // name (events.beforeMount, events.mounted, ...) without checking
          // that chart.events itself is an object first — an explicit
          // `events: undefined` key throws on that first access, `events` not
          // being present at all doesn't.
          ...(onSelect
            ? {
                events: {
                  dataPointSelection: (event, ctx, cfg) => {
                    const stage = contactStages[cfg.dataPointIndex];
                    if (stage) onSelect(stage);
                  }
                }
              }
            : {})
        },
        ...(isLine
          ? {
              stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
              markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
              fill: { type: "solid", opacity: 0.2 }
            }
          : {
              plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
            }),
        // Per-bar colours require `distributed`, which the bar path already sets.
        colors: highlightStage
          ? contactStages.map(stage => (stage.stage === highlightStage ? COLOR : MUTED))
          : [COLOR],
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: labels,
          labels: showAxes
            ? { show: true, rotate: -35, trim: true, hideOverlappingLabels: true, style: { fontSize: "11px" } }
            : { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: niceMax,
          tickAmount,
          labels: showAxes ? { show: true, style: { fontSize: "11px" } } : { show: false }
        },
        grid: {
          borderColor: "#e8ebee",
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        tooltip: {
          x: { show: true },
          y: { formatter: formatTooltipY }
        }
      };
      const series = [{ name: "Contacts", data: counts }];
      // Keyed on category count ONLY, not on bar/line type. ApexCharts corrupts
      // its internal DOM bookkeeping when series/categories length changes on an
      // already-mounted instance (a date-filter change can shrink/grow the stage
      // list), so that case needs a forced remount. But ApexCharts' initial
      // mount/draw is asynchronous (Promise-based) — if bar<->line also forced a
      // remount here, toggling the visualization switch unmounts the chart while
      // its in-flight mount() is still pending, and that promise later resolves
      // against DOM refs destroy() already tore down, throwing as an unhandled
      // rejection (invisible to React error boundaries — see ChartFailSafe
      // above). react-apexcharts already handles a `type`/`options` prop change
      // reactively via updateOptions() without a remount, so let that path run
      // instead of forcing one.
      const chartKey = `stages-${contactStages.length}`;
      return (
        <ReactApexChart
          key={chartKey}
          options={options}
          series={series}
          type={isLine ? "area" : "bar"}
          height={140}
          width="100%"
        />
      );
    })()
  );

  return (
    <div className="contactStagesWrap">
      <p className="contactStagesSnapshotLabel">Live snapshot</p>
      <MemberBreakdownList rows={toMemberTotals(memberBreakdown)} loading={breakdownLoading} />
      {/* Stays mounted across bar/line/KPI switches — see ChartFailSafe's doc
          comment for why it can't be placed inside the conditional it guards. */}
      <ChartFailSafe resetKey={`${visualization}-${contactStages.length}`}>{chartBody}</ChartFailSafe>
    </div>
  );
};

export default ContactStagesWidget;
